<template>
  <div>
    <page-header>
      <template #title>
        <h1 class="page-title">Contacts For Company: {{ companyName }}</h1>
      </template>
      <template #actions>
        <page-header-action tag="a" href="#" @click.prevent="openContactModal">
          Add Contact
        </page-header-action>
      </template>
    </page-header>
    <div class="row">
      <div class="col-lg-12">
        <div class="card mb-3">
          <table class="table carpenter">
            <thead>
              <tr>
                <th>First Name</th>
                <th>Last Name</th>
                <th>Email Address</th>
                <th>Phone Number</th>
                <th>&nbsp;</th>
              </tr>
            </thead>
            <tbody>
              <tr v-bind:key="index" v-for="(contact, index) in contacts">
                <td>{{ contact.first_name }}</td>
                <td>{{ contact.last_name }}</td>
                <td>{{ contact.email_address }}</td>
                <td>{{ contact.phone_number }}</td>
                <td>
                  <button
                    class="btn btn-primary btn-sm"
                    v-on:click.prevent="editContact(contact)"
                  >
                    Edit
                  </button>
                  <button
                    class="btn btn-danger btn-sm"
                    v-on:click.prevent="deleteContact(contact)"
                  >
                    Delete
                  </button>
                </td>
              </tr>
            </tbody>
          </table>
        </div>
      </div>
    </div>

    <modal :show="modal" :large="true" v-on:close="cancel">
      <h4 slot="title" v-text="modalHeaderText"></h4>

      <inputbox
        name="first_name"
        label="First Name"
        :show-error="form.errors.has('first_name')"
        :error-message="form.errors.get('first_name')"
        helper="Please enter the first name of the contact"
        v-model="form.first_name"
      ></inputbox>

      <inputbox
        name="last_name"
        label="Last Name"
        :show-error="form.errors.has('last_name')"
        :error-message="form.errors.get('last_name')"
        helper="Please enter the last name of the contact"
        v-model="form.last_name"
      ></inputbox>

      <inputbox
        name="email_address"
        label="Email Address"
        :show-error="form.errors.has('email_address')"
        :error-message="form.errors.get('email_address')"
        helper="Please enter the first name of the contact"
        v-model="form.email_address"
        type="email"
      ></inputbox>

      <inputbox
        name="phone_number"
        label="Phone Number"
        :show-error="form.errors.has('phone_number')"
        :error-message="form.errors.get('phone_number')"
        helper="Please enter the phone number of the contact"
        v-model="form.phone_number"
      ></inputbox>

      <div class="footer" slot="footer">
        <button class="btn btn-danger float-left" v-on:click.prevent="cancel">
          Close
        </button>
        <form-button :indicator="indicator" v-on:clicked="saveContact"
          >Save Contact</form-button
        >
      </div>
    </modal>
  </div>
</template>

<script>
import PageHeader from './../Shared/PageHeader.vue';
import PageHeaderAction from './../Shared/PageHeaderAction.vue';

export default {
  components: {
    PageHeader,
    PageHeaderAction
  },

  props: {
    company: {
      type: String,
      required: true
    }
  },

  data() {
    return {
      companyDetails: null,
      contacts: [],
      indicator: false,
      modal: false,
      editingIdentifier: null,
      form: new window.Form({
        first_name: null,
        last_name: null,
        email_address: null,
        phone_number: null
      })
    };
  },

  computed: {
    companyName() {
      return this.companyDetails ? this.companyDetails.name : '';
    },

    modalHeaderText() {
      return this.editingIdentifier ? 'Edit Contact' : 'Add Contact';
    }
  },

  mounted() {
    window.Ajax.get(`/companies/${this.company}`)
      .then(response => {
        this.companyDetails = response.data;

        window.Ajax.get(`/companies/${this.company}/contacts`)
          .then(response => {
            this.contacts = response.data;
          })
          .catch(error => {});
      })
      .catch(error => {});
  },

  methods: {
    editContact(contact) {
      this.form.first_name = contact.first_name;
      this.form.last_name = contact.last_name;
      this.form.email_address = contact.email_address;
      this.form.phone_number = contact.phone_number;
      this.editingIdentifier = contact.identifier;

      this.openContactModal();
    },

    openContactModal() {
      this.modal = true;
    },

    cancel() {
      this.modal = false;
      this.form.reset();
      this.editingIdentifier = null;
      this.indicator = false;
    },

    saveContact() {
      this.indicator = true;
      let method = 'create';

      if (this.editingIdentifier) {
        method = 'update';
      }

      this[method]()
        .then(response => {
          this.handleSuccess(response);
        })
        .catch(error => {
          this.handleError();
        });
    },

    handleSuccess(response) {
      window.toastify.success(
        'Success!',
        'The contact was saved successfully.'
      );

      if (this.editingIdentifier) {
        let index = this.contacts.findIndex(item => {
          return item.identifier === this.editingIdentifier;
        });

        this.contacts[index] = response;

        this.cancel();
        return;
      }

      this.contacts.push(response);
      this.cancel();
    },

    handleError() {
      window.toastify.error(
        'Error!',
        'Sorry, there was an error saving the contact.'
      );
    },

    update() {
      return new Promise((resolve, reject) => {
        this.form
          .put(`/companies/${this.company}/contacts/${this.editingIdentifier}`)
          .then(response => {
            resolve(response);
          })
          .catch(error => {
            reject(error);
          });
      });
    },

    create() {
      return new Promise((resolve, reject) => {
        this.form
          .post(`/companies/${this.company}/contacts`)
          .then(response => {
            resolve(response);
          })
          .catch(error => {
            reject(error);
          });
      });
    },

    deleteContact(contact) {
      window
        .swal({
          title: 'Are you sure?',
          text: "You won't be able to revert this!",
          type: 'warning',
          showCancelButton: true,
          confirmButtonText: 'Yes, delete the contact!'
        })
        .then(() => {
          window.Ajax.delete(
            `/companies/${this.company}/contacts/${contact.identifier}`
          )
            .then(response => {
              let index = this.contacts.findIndex(item => {
                return item.identifier === contact.identifier;
              });

              this.contacts.splice(index, 1);
              window.toastify.success(
                'Deleted!',
                'The contact was deleted successfully!.'
              );
            })
            .catch(err => {
              window.toastify.error(
                'Error',
                'Sorry, there was an issue deleting the contact.'
              );
            })
            .catch(() => {});
        });
    }
  }
};
</script>
